// ─── OfflineBanner — état réseau visible (sensation native) ──────────────────
// Audit #9 : aucune détection online/offline aujourd'hui → échecs réseau silencieux.
// Bannière discrète quand la connexion tombe, + court flash « reconnecté » au retour.
// Zéro dépendance, respecte la safe-area (encoches), n'interfère avec rien.

import React, { useEffect, useRef, useState } from "react";

export default function OfflineBanner() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [justReconnected, setJustReconnected] = useState(false);
  // Garde l'id du timer du flash « reconnecté » pour pouvoir l'annuler :
  // sans ça, un setTimeout en vol après démontage ferait un setState sur un
  // composant démonté (warning React + fuite). On l'annule au nouveau flash
  // ET au cleanup (Qodo : timer non nettoyé).
  const flashTimer = useRef(null);

  useEffect(() => {
    const goOffline = () => setOnline(false);
    const goOnline = () => {
      setOnline(true);
      setJustReconnected(true);
      // Le flash « reconnecté » disparaît tout seul ; on annule un éventuel
      // flash précédent encore en vol avant d'en reprogrammer un.
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setJustReconnected(false), 2500);
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
      if (flashTimer.current) clearTimeout(flashTimer.current);
    };
  }, []);

  if (online && !justReconnected) return null;

  const offline = !online;
  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed left-0 right-0 z-[10050] flex items-center justify-center gap-2 px-4 text-sm font-semibold text-white transition-transform ${
        offline ? "bg-slate-800" : "bg-emerald-600"
      }`}
      style={{
        top: 0,
        paddingTop: "calc(8px + env(safe-area-inset-top))",
        paddingBottom: 8,
      }}
      data-testid="offline-banner"
    >
      {offline ? (
        <>
          <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          Pas de connexion — certaines actions sont indisponibles.
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Connexion rétablie.
        </>
      )}
    </div>
  );
}
